"use client";

import { useCallback, useState } from 'react';
import { AuthModalState } from '@/types/auth-types';
import type { AuthError } from '@/types/auth-types';
import { useAuthLegalModalState } from './useAuthLegalModalState';
import { useEmailAuthHandlers } from './useEmailAuthHandlers';
import { useProviderAuthHandlers } from './useProviderAuthHandlers';
import { useSyncedAuthModalState } from './useSyncedAuthModalState';

interface UseCauthmodalStateInput {
  initialState: AuthModalState;
}

export interface CauthmodalState {
  email: string;
  emailInputError: string;
  error: AuthError | null;
  hasOpenedPolicy: boolean;
  hasOpenedTerms: boolean;
  loadingApple: boolean;
  loadingEmail: boolean;
  loadingGoogle: boolean;
  modalState: AuthModalState;
  showPolicy: boolean;
  showTerms: boolean;
  handleAppleAuth: () => void;
  handleClosePolicy: () => void;
  handleCloseTerms: () => void;
  handleEmailAuth: () => void;
  handleEmailChange: (value: string) => void;
  handleGoogleAuth: () => void;
  handleOpenTerms: () => void;
  handlePrivacyClick: () => void;
  handleRetry: () => void;
}

export function useCauthmodalState({
  initialState,
}: UseCauthmodalStateInput): CauthmodalState {
  const { modalState, setModalState } = useSyncedAuthModalState({ initialState });
  const [error, setError] = useState<AuthError | null>(null);

  const emailAuth = useEmailAuthHandlers({
    setError,
    setModalState,
  });
  const providerAuth = useProviderAuthHandlers({
    setError,
    setModalState,
  });
  const legalModal = useAuthLegalModalState();

  const handleRetry = useCallback((): void => {
    setError(null);
    setModalState(AuthModalState.Initial);
  }, [setModalState]);

  return {
    email: emailAuth.email,
    emailInputError: emailAuth.emailInputError,
    error,
    hasOpenedPolicy: legalModal.hasOpenedPolicy,
    hasOpenedTerms: legalModal.hasOpenedTerms,
    loadingApple: providerAuth.loadingApple,
    loadingEmail: emailAuth.loadingEmail,
    loadingGoogle: providerAuth.loadingGoogle,
    modalState,
    showPolicy: legalModal.showPolicy,
    showTerms: legalModal.showTerms,
    handleAppleAuth: providerAuth.handleAppleAuth,
    handleClosePolicy: legalModal.handleClosePolicy,
    handleCloseTerms: legalModal.handleCloseTerms,
    handleEmailAuth: emailAuth.handleEmailAuth,
    handleEmailChange: emailAuth.handleEmailChange,
    handleGoogleAuth: providerAuth.handleGoogleAuth,
    handleOpenTerms: legalModal.handleOpenTerms,
    handlePrivacyClick: legalModal.handlePrivacyClick,
    handleRetry,
  };
}
